import {
  Relative,
  Scrollbar,
  useDateFormat,
  Text,
  VStack,
  Flex,
  Box,
} from '@revolut-internal/ui-kit'
import React, { useEffect, useMemo, useRef } from 'react'

import { ErrorWidget, NotFoundWidget } from 'components'
import { AuthorRole } from 'lib/apiClient'
import { groupMessagesByDate } from 'lib/utils/messages'
import { useProcessDebugDetailsContext } from 'pages/AiProcessDebugV2/hooks/useProcessDebugDetailsContext'

import { DebugMessagesLoading } from './DebugMessagesLoading'
import { CollapsibleDebugChatMessage, SimpleDebugChatMessage } from '../DebugChatMessage'

export const DebugMessages = () => {
  const initialScroll = useRef(false)

  const {
    chatMessages,
    setSelectedMessageId,
    selectedMessageId,
    isLoadingDialogueSnapshots,
    isDialogueSnapshotsError,
  } = useProcessDebugDetailsContext()

  const dateFormat = useDateFormat()

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const messageScrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!initialScroll.current && messageScrollRef.current) {
      messageScrollRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
      initialScroll.current = true
    }
  }, [selectedMessageId])

  const groupedMessages = useMemo(
    () => groupMessagesByDate(chatMessages, dateFormat),
    [chatMessages, dateFormat],
  )

  if (isLoadingDialogueSnapshots) {
    return <DebugMessagesLoading />
  }

  if (isDialogueSnapshotsError) {
    return (
      <Flex height="100%" alignItems="center" justifyContent="center">
        <ErrorWidget title="An error occured" />
      </Flex>
    )
  }

  if (!Object.keys(groupedMessages).length) {
    return (
      <Flex height="100%" alignItems="center" justifyContent="center">
        <NotFoundWidget
          title="No data found"
          description="Dialog snapshot contains no messages"
        />
      </Flex>
    )
  }

  return (
    <Relative height="100%">
      <VStack
        ref={scrollRef}
        height="100%"
        p="s-16"
        space="s-16"
        data-testid="debug-messages-list"
      >
        {Object.keys(groupedMessages).map(key => (
          <React.Fragment key={key}>
            <Text variant="body3" textAlign="center" color="grey-50">
              {key}
            </Text>
            {groupedMessages[key].map(message => {
              if (
                message.messageId === selectedMessageId &&
                message.authorRole === AuthorRole.Customer
              ) {
                return (
                  <Box
                    ref={messageScrollRef}
                    id={message.messageId + message.authorRole}
                    key={message.messageId + message.authorRole}
                  >
                    <CollapsibleDebugChatMessage message={message} isSelected />
                  </Box>
                )
              }

              return (
                <SimpleDebugChatMessage
                  key={message.messageId + message.authorRole}
                  message={message}
                  onClick={
                    message.authorRole === AuthorRole.Customer
                      ? () => setSelectedMessageId(message.messageId)
                      : undefined
                  }
                />
              )
            })}
          </React.Fragment>
        ))}
      </VStack>
      <Scrollbar scrollRef={scrollRef} />
    </Relative>
  )
}
